/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : NetworkInterfaces.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Stack, Typography, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../redux/actions";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";

import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomRadioButtonV2 from "../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectVariantColor from "../../components/customComponentsV2/common/colorpicker/CustomSelectVariantColor";
import * as XLSX from "xlsx";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getNetworkInterfaceList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

function NetworkInterfaces({ open, handleClick }) {
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    if (open === "network-interfaces") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      setTableKey((prev) => prev + 1);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const networkRef = useRef(null);
  const [openAddNetworkInterfaces, setOpenAddNetworkInterfaces] =
    useState(false);
  const [openEditNetworkInterfaces, setOpenEditNetworkInterfaces] =
    useState(false);
  const [selectedNetworkInterface, setSelectedNetworkInterface] =
    useState(null);

  useEffect(() => {
    if (open === "12") {
      const timeout = setTimeout(() => {
        if (
          networkRef.current &&
          networkRef.current.scrollHeight > networkRef.current.clientHeight
        ) {
          networkRef.current.scrollTop = networkRef.current.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_network_interfaces,
        show_toast: false,
      }),
    );
  }, [dispatch]);

  const networkDialog = (open) => {
    setOpenAddNetworkInterfaces(open);
  };

  const handleAddNetworkInterfaces = () => {
    setColor("#000000");
    networkDialog(true);
  };

  const [searchQuery] = useState("");

  const handleEditClick = (params) => {
    console.log("Edit clicked for row:", params);
    setSelectedNetworkInterface(params.row);
    setEditName(params.row.name);
    setEditModuleType(params.row.network_type);
    setEditColor(params.row.color || "");
    setOpenEditNetworkInterfaces(true);
  };

  const [name, setName] = useState("");
  const [moduleType, setModuleType] = useState("ecu");
  const [color, setColor] = useState("#000000");

  const [editName, setEditName] = useState("");
  const [editModuleType, setEditModuleType] = useState("ecu");
  const [editColor, setEditColor] = useState("");

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalNetworkInterfacesCount, setTotalNetworkInterfacesCount] =
    useState(0);
  const [networkInterfacesList, setNetworkInterfacesList] = useState([]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return networkInterfacesList || [];
    return (networkInterfacesList || []).filter((row) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, networkInterfacesList]);

  const COLUMNS = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
        sortable: false,
        renderCell: (params) => {
          const index = filteredRows.findIndex(
            (item) => item.id === params.row.id,
          );
          if (index === -1) return "";
          return String((page - 1) * rowsPerPage + index + 1);
        },
      },
      {
        field: "name",
        headerName: "Network Interface",
        flex: 0.8,
        minWidth: 120,
        sortable: true,
      },
      {
        field: "network_type",
        headerName: "Type",
        flex: 0.7,
        minWidth: 80,
        sortable: true,
        renderCell: (params) => {
          const v = (params.value || "").toString().toLowerCase();
          if (v === "ecu") return "EE Component";
          if (v === "swm") return "Software Module";
          return params.value || "N/A";
        },
      },
      {
        field: "color",
        headerName: "Color",
        flex: 0.4,
        minWidth: 70,
        sortable: false,
        renderCell: (params) => {
          if (!params.value) return "N/A";
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "4px",
                  bgcolor: params.value,
                  border: "1px solid #ccc",
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.8,
        minWidth: 130,
        sortable: true,
        renderCell: (params) => {
          if (params.value) {
            return convertUtcToTimeZone(
              params.value,
              timeZoneDetails?.profile?.profile?.region,
            );
          }
          return "N/A";
        },
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        flex: 0.8,
        minWidth: 130,
        sortable: true,
        renderCell: (params) => {
          if (params.value) {
            return convertUtcToTimeZone(
              params.value,
              timeZoneDetails?.profile?.profile?.region,
            );
          }
          return "N/A";
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [timeZoneDetails, page, rowsPerPage, filteredRows],
  );

  const getNetworkInterfacesDetails = React.useCallback(async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_network_interfaces,
        show_toast: false,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );
    if (resp?.error) {
      setNetworkInterfacesList([]);
      setTotalNetworkInterfacesCount(0);
      return;
    }

    const list = getNetworkInterfaceList(resp);

    setNetworkInterfacesList(list);
    setTotalNetworkInterfacesCount(
      resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0),
    );
  }, [dispatch, page, rowsPerPage]);

  useEffect(() => {
    getNetworkInterfacesDetails();
  }, [getNetworkInterfacesDetails]);

  useEffect(() => {
    setPage(1); // Reset to first page when search changes
  }, [searchQuery]);

  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      console.warn("No data to export");
      return;
    }

    const exportData = filteredRows.map((row, index) => ({
      "S.No": index + 1,
      "Network Interface": row.name,
      Type: row.network_type?.toUpperCase() || "N/A",
      Color: row.color || "N/A",
      Created: row.created_at
        ? convertUtcToTimeZone(
            row.created_at,
            timeZoneDetails?.profile?.profile?.region,
          )
        : "N/A",
      Updated: row.updated_at
        ? convertUtcToTimeZone(
            row.updated_at,
            timeZoneDetails?.profile?.profile?.region,
          )
        : "N/A",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    XLSX.utils.book_append_sheet(wb, ws, "Network Interfaces");

    XLSX.writeFile(wb, "Network_Interfaces.xlsx");
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Network Interface" },
  ];

  const handleClose = () => {
    networkDialog(false);
    setName("");
    setModuleType("ecu");
    setColor("#000000");
  };

  const handleEditClose = () => {
    setOpenEditNetworkInterfaces(false);
    setEditName("");
    setEditModuleType("");
    setEditColor("");
    setSelectedNetworkInterface(null);
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        name: name,
        network_type: moduleType.toLowerCase(), // Convert EE/SWM to ee/swm
        ...(color && { color }),
      };
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_network_interfaces,
          params: reqObj,
          show_toast: true,
        }),
      );
      networkDialog(false);

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_network_interfaces,
          show_toast: false,
          params: {
            page_number: 1,
            page_size: rowsPerPage,
          },
        }),
      );
      if (resp?.error) {
        setNetworkInterfacesList([]);
        setTotalNetworkInterfacesCount(0);
      } else {
        const list = getNetworkInterfaceList(resp);

        setNetworkInterfacesList(list);
        setTotalNetworkInterfacesCount(
          resp?.total_count ??
            resp?.totalItems ??
            resp?.total ??
            (Array.isArray(list) ? list.length : 0),
        );
      }
      setPage(1);

      setName("");
      setModuleType("ecu");
      setColor("#000000");
    } catch (error) {
      console.error("Error adding network interface:", error);
    }
  };

  const handleClickUpdate = async () => {
    try {
      const reqObj = {
        id: selectedNetworkInterface.id,
        name: editName,
        network_type: editModuleType.toLowerCase(),
        ...(editColor !== undefined && { color: editColor }),
      };
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_network_interfaces,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (!resp.error) {
        handleEditClose();
      }

      await getNetworkInterfacesDetails();
    } catch (error) {
      console.error("Error updating network interface:", error);
    }
  };
  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_network_interfaces}
              searchKey={"name"}
              getDataOnSearch={(data, resp) => {
                if (resp?.error) {
                  setNetworkInterfacesList([]);
                  setTotalNetworkInterfacesCount(0);
                  return;
                }

                const searchedList = Array.isArray(data) ? data : [];
                setNetworkInterfacesList(searchedList);
                setTotalNetworkInterfacesCount(
                  resp?.total_count ??
                    resp?.totalItems ??
                    resp?.total ??
                    searchedList.length,
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddNetworkInterfaces} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: "76vh", // ? Takes 78% of screen height
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`network-interfaces-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={filteredRows}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={handleEditClick}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalNetworkInterfacesCount}
        />
      </Box>

      <CustomDialogBoxV2
        open={openAddNetworkInterfaces}
        onClose={handleClose}
        title={"Add Network Interface"}
        successButtonName={"Add"}
        rejectButtonName={"Close"}
        onClickSuccess={handleClickAdd}
        onDisabled={!name || name.trim().length < 3 || !moduleType}
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", color: "#848587" }}
            >
              Type<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ ml: 2, mt: 0.5 }}>
              <CustomRadioButtonV2
                displayDirection="row"
                value={moduleType}
                onChange={(e) => setModuleType(e.target.value)}
                data={[
                  { value: "ecu", label: "EE Component" },
                  { value: "swm", label: "Software Module" },
                ]}
              />
            </Box>
          </Box>
          <CustomFormTextFieldV2
            label="Network Interface"
            required={true}
            value={name}
            setValue={(val) => {
              if (val.length <= 50) {
                setName(val);
              }
            }}
            maxLength={50}
          />
          <CustomSelectVariantColor
            label="Select Color"
            value={color}
            setValue={setColor}
          />
        </Stack>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={openEditNetworkInterfaces}
        onClose={handleEditClose}
        title={"Update Network Interface"}
        successButtonName={"Update"}
        rejectButtonName={"Close"}
        onClickSuccess={handleClickUpdate}
        onDisabled={!editColor}
        actionButtonStyle={"continuous"}
        onClickReject={handleEditClose}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", color: "#848587" }}
            >
              Type<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ ml: 2, mt: 0.5 }}>
              <CustomRadioButtonV2
                displayDirection="row"
                value={editModuleType}
                onChange={(e) => setEditModuleType(e.target.value)}
                disabled={true}
                data={[
                  { value: "ecu", label: "EE Component" },
                  { value: "swm", label: "Software Module" },
                ]}
              />
            </Box>
          </Box>
          <CustomFormTextFieldV2
            label="Network Interface"
            required={true}
            value={editName}
            setValue={setEditName}
            disabled={true}
          />
          <CustomSelectVariantColor
            label="Color"
            value={editColor}
            setValue={setEditColor}
          />
        </Stack>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default NetworkInterfaces;

NetworkInterfaces.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};
